/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : index.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */


import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { availableColumns, attachedColumns } from "./columns";
import {
  HandleApiActions,
  legalRequirements,
} from "../../../../../redux/actions";
import ruleEngineActions from "../../../../../redux/actions/ruleEngineAction";
import CustomDataGridV2 from "../../../../../components/customComponentsV2/common/datagrid/CustomDataGridV2";

const LEGAL_SCOPE_OPTIONS = [
  { value: "LEGAL_STANDARD", label: "Legal Standards" },
];

const mapAttachedItem = (item) => ({
  id: item.target_id, // DataGrid identity
  standard_code: item.reference_code, // column: Standard
  name: item.display_name || "-", // column: Chapter
  description: item.description, // column: Description
  _raw: item,
});

const mapAvailableItem = (item) => ({
  id: item.ls_id, // DataGrid identity
  standard_code: item.standard_number, // "Standard"
  name: item.name, // "Chapter"
  description: item.description, // "Description"
  _raw: item,
});

const filterAvailableItems = (data, attachedIds) =>
  (data || [])
    .filter((item) => !attachedIds.has(item.ls_id))
    .map(mapAvailableItem);

const addIdToList = (list, idToAdd) =>
  list.includes(idToAdd) ? list : [...list, idToAdd];

const removeIdFromList = (list, idToRemove) =>
  list.filter((id) => id !== idToRemove);

function StepLegalScope({ ruleSetId, value = {}, onChange }) {
  const dispatch = useDispatch();

  const [selectedScope, setSelectedScope] = useState("");
  const [availableItems, setAvailableItems] = useState([]);
  const [attachedItems, setAttachedItems] = useState([]);
  const [locallyAttachedIds, setLocallyAttachedIds] = useState([]);

  const [availableLegalItemsPage, setAvailableLegalItemsPage] = useState(1);
  const [availableLegalItemsTotalCount, setAvailableLegalItemsTotalCount] =
    useState(0);
  const [attachedLegalItemsPage, setAttachedLegalItemsPage] = useState(1);
  const [attachedLegalItemsTotalCount, setAttachedLegalItemsTotalCount] =
    useState(0);

  const PAGE_SIZE = 5;

  const getAttachedLegalScopeItems = () => {
    const api = {
      ...ruleEngineActions.get_attached_legal_items,
      api_name: ruleEngineActions.get_attached_legal_items.api_name.replaceAll(
        ":ruleSetId",
        ruleSetId,
      ),
    };
    dispatch(
      HandleApiActions({
        ...api,
        params: {
          page_size: PAGE_SIZE,
          page_number: attachedLegalItemsPage,
        },
        callback: (resp) => {
          if (!resp?.error) {
            const attachedItemsForDisplay = (resp.data || []).map(
              mapAttachedItem,
            );
            if (attachedItemsForDisplay.length > 0) {
              setSelectedScope(LEGAL_SCOPE_OPTIONS?.[0]?.value);
              setAttachedItems(attachedItemsForDisplay);
            } else {
              setSelectedScope(LEGAL_SCOPE_OPTIONS?.[0]?.value);
              setAttachedItems([]);
            }
            const nextTotalCount = Number(resp.total_count) || 0;
            const maxPage = Math.max(
              1,
              Math.ceil(nextTotalCount / PAGE_SIZE),
            );

            setAttachedLegalItemsTotalCount(nextTotalCount);

            if (attachedLegalItemsPage > maxPage) {
              setAttachedLegalItemsPage(maxPage);
            }
          }
        },
      }),
    );
  };

  const getAvailableLegalScopeItems = () => {
    dispatch(
      HandleApiActions({
        ...legalRequirements.get_legal_standard_main_details,
        params: {
          page_number: availableLegalItemsPage,
          page_size: PAGE_SIZE,
          sort_by: "ASC",
          sort_column: "created_at",
          exclude_attached_rule_set_id: ruleSetId,
        },
        show_toast: false,
        callback: (resp) => {
          if (!resp?.error) {
            const attachedIds = new Set(
              attachedItems.map((i) => i.id).concat(locallyAttachedIds),
            );
            const availableItemsForDisplay = filterAvailableItems(
              resp.data,
              attachedIds,
            );

            const nextTotalCount = Number(resp.total_count) || 0;
            const maxPage = Math.max(
              1,
              Math.ceil(nextTotalCount / PAGE_SIZE),
            );

            setAvailableLegalItemsTotalCount(nextTotalCount);

            if (availableLegalItemsPage > maxPage) {
              setAvailableLegalItemsPage(maxPage);
              return;
            }

            if (
              availableItemsForDisplay.length === 0 &&
              nextTotalCount > 0 &&
              availableLegalItemsPage < maxPage
            ) {
              setAvailableLegalItemsPage((prev) => prev + 1);
              return;
            }

            setAvailableItems(availableItemsForDisplay);
          }
        },
      }),
    );
  };

  const handleAvailableLegalItemsPageChange = (page) => {
    if (page > 0) {
      setAvailableLegalItemsPage(page);
    }
  };

  const handleAttachedLegalItemsPageChange = (page) => {
    if (page > 0) {
      setAttachedLegalItemsPage(page);
    }
  };

  useEffect(() => {
    getAttachedLegalScopeItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ruleSetId) return;
    if (!selectedScope) return;

    if (!availableItems) {
      getAvailableLegalScopeItems();
    }
    if (!attachedItems) {
      getAttachedLegalScopeItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScope]);

  useEffect(() => {
    if (!ruleSetId) return;
    getAvailableLegalScopeItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableLegalItemsPage, attachedItems, locallyAttachedIds]);

  useEffect(() => {
    if (!ruleSetId) return;
    getAttachedLegalScopeItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachedLegalItemsPage]);

  useEffect(() => {
    onChange?.({
      scope: selectedScope,
      attached: attachedItems,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScope, attachedItems]);

  const handleAttach = async (row) => {
    const api = {
      ...ruleEngineActions.attach_legal_item,
      api_name: ruleEngineActions.attach_legal_item.api_name.replaceAll(
        ":ruleSetId",
        ruleSetId,
      ),
    };
    await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...api,
          params: {
            linkType: selectedScope,
            targetId: row.id,
          },
          show_toast: true,
          callback: (resp) => {
            if (!resp?.error) {
              setLocallyAttachedIds((prev) => addIdToList(prev, row.id));
              getAttachedLegalScopeItems();
              getAvailableLegalScopeItems();
            }
          },
        }),
      ),
    );
  };

  const handleDetach = async (row) => {
    const api = {
      ...ruleEngineActions.detach_legal_item,
      api_name: ruleEngineActions.detach_legal_item.api_name
        .replaceAll(":ruleSetId", ruleSetId)
        .replaceAll(":linkType", selectedScope)
        .replaceAll(":targetId", row.id),
    };
    await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...api,
          show_toast: true,
          callback: (resp) => {
            if (!resp?.error) {
              setLocallyAttachedIds((prev) => removeIdFromList(prev, row.id));
              getAvailableLegalScopeItems();
              getAttachedLegalScopeItems();
            }
          },
        }),
      ),
    );
  };

  useEffect(() => {
    setLocallyAttachedIds([]);
  }, [ruleSetId]);

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{ mb: 4, display: "flex", alignItems: "start" }}
      >
        Attach/Detach Legal Standards to Rule set
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "1fr 1fr",
            lg: "1fr 1fr",
          },
          gap: 3,
          minHeight: 500,
          height: 500,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <Typography sx={{ mb: 1 }}>Available</Typography>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              overflow: "hidden",
              maxHeight: "370px",
            }}
          >
            <CustomDataGridV2
              rows={availableItems}
              columns={availableColumns(handleAttach)}
              hideHeader={false}
              rowsPerPage={5}
              page={availableLegalItemsPage}
              showPagination={true}
              handlePageChange={handleAvailableLegalItemsPageChange}
              totalItems={availableLegalItemsTotalCount}
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <Typography sx={{ mb: 1 }}>Attached</Typography>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              overflow: "hidden",
              maxHeight: "370px",
            }}
          >
            <CustomDataGridV2
              rows={attachedItems}
              columns={attachedColumns(handleDetach)}
              hideHeader={false}
              rowsPerPage={5}
              page={attachedLegalItemsPage}
              showPagination={true}
              totalItems={attachedLegalItemsTotalCount}
              handlePageChange={handleAttachedLegalItemsPageChange}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

StepLegalScope.propTypes = {
  ruleSetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.object,
  onChange: PropTypes.func,
};

export default StepLegalScope;
